import React from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { User, Phone, BookOpen, Briefcase, Building, MapPin, UserPlus, Mail, Shield, Award } from 'lucide-react';

function AddInspectors() {
    const [formData, setFormData] = React.useState({
        name: '',
        phone: '',
        fieldofstudy: '',
        position: '',
        office: '',
        currenaddress: ''
    });

    const [isSubmitting, setIsSubmitting] = React.useState(false);

    // Create axios instance with proper CORS and token configuration
    const api = axios.create({
        baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000/api',
        timeout: 30000,
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        },
        withCredentials: false
    });

    // Request interceptor to attach token
    api.interceptors.request.use(
        (config) => {
            const token = localStorage.getItem('token');
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
            return config;
        },
        (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    api.interceptors.response.use(
        (response) => response,
        (error) => {
            if (error.code === 'ECONNABORTED') {
                console.error('Request timeout');
            } else if (error.response) {
                console.error('Server error:', error.response.status);
                if (error.response.status === 401) {
                    localStorage.removeItem('auth_token');
                }
            } else if (error.request) {
                console.error('No response received - Possible CORS issue');
            }
            return Promise.reject(error);
        }
    );

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                Swal.fire({
                    title: 'Authentication Error',
                    text: 'Please login to continue',
                    icon: 'warning',
                    confirmButtonText: 'OK',
                    confirmButtonColor: '#4F46E5'
                });
                setIsSubmitting(false);
                return;
            }

            const response = await api.post('/addinspector', formData);

            if (response.status === 200 || response.status === 201) {
                await Swal.fire({
                    title: 'Success!',
                    text: 'Inspector added successfully',
                    icon: 'success',
                    confirmButtonText: 'OK',
                    confirmButtonColor: '#4F46E5',
                    timer: 3000,
                    timerProgressBar: true
                });

                setFormData({
                    name: '',
                    phone: '',
                    fieldofstudy: '',
                    position: '',
                    office: '',
                    currenaddress: ''
                });
            }
        } catch (error) {
            console.error('Full error object:', error);

            let errorMessage = 'Failed to add inspector';
            let errorTitle = 'Error!';

            if (error.code === 'ECONNABORTED') {
                errorMessage = 'Request timeout. Please try again.';
            } else if (error.response) {
                if (error.response.status === 401) {
                    errorTitle = 'Unauthorized';
                    errorMessage = 'Your session has expired. Please login again.';
                } else if (error.response.status === 403) {
                    errorTitle = 'Forbidden';
                    errorMessage = 'You do not have permission to perform this action.';
                } else if (error.response.status === 404) {
                    errorMessage = 'API endpoint not found. Please check the URL.';
                } else if (error.response.status === 500) {
                    errorMessage = 'Server error. Please try again later.';
                } else {
                    errorMessage = error.response.data?.message || errorMessage;
                }
            } else if (error.request) {
                errorMessage = 'No response from server. Please check your connection.';
            } else {
                errorMessage = error.message || errorMessage;
            }

            Swal.fire({
                title: errorTitle,
                text: errorMessage,
                icon: 'error',
                confirmButtonText: 'OK',
                confirmButtonColor: '#EF4444'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const inputFields = [
        { id: 'name', label: 'Full Name', icon: User, type: 'text', placeholder: 'Enter full name', required: true },
        { id: 'phone', label: 'Phone Number', icon: Phone, type: 'tel', placeholder: 'Enter phone number', required: true },
        { id: 'fieldofstudy', label: 'Field of Study', icon: BookOpen, type: 'text', placeholder: 'e.g., Civil Engineering', required: true },
        { id: 'position', label: 'Position', icon: Briefcase, type: 'text', placeholder: 'e.g., Senior Inspector', required: true },
        { id: 'office', label: 'Office', icon: Building, type: 'text', placeholder: 'e.g., Main Branch', required: true },
        { id: 'currenaddress', label: 'Current Address', icon: MapPin, type: 'text', placeholder: 'Enter current address', required: true }
    ];

    return (
        <div className='min-h-screen bg-gradient-to-br from-indigo-900 via-indigo-800 to-indigo-700 py-12 px-4 sm:px-6 lg:px-8'>
            <div className='max-w-4xl mx-auto'>
                {/* Header Section */}
                <div className='text-center mb-10'>
                    <div className='inline-flex items-center justify-center w-24 h-24 rounded-full bg-white/10 backdrop-blur-sm mb-4 shadow-xl border-2 border-white/30'>
                        <UserPlus className='w-12 h-12 text-white' />
                    </div>
                    <h1 className='text-4xl md:text-5xl font-bold text-white mb-3 drop-shadow-lg'>
                        Add New Inspector
                    </h1>
                    <p className='text-indigo-100 text-lg max-w-2xl mx-auto'>
                        Fill in the details below to register a new inspector to the system
                    </p>
                </div>

                {/* Main Form Card */}
                <div className='bg-white/10 backdrop-blur-md rounded-2xl shadow-2xl border border-white/20 overflow-hidden'>
                    {/* Card Header */}
                    <div className='bg-gradient-to-r from-indigo-700 to-indigo-800 px-8 py-5 border-b border-white/20'>
                        <div className='flex items-center space-x-3'>
                            <Shield className='w-6 h-6 text-white' />
                            <h2 className='text-xl font-bold text-white'>Inspector Registration Form</h2>
                        </div>
                        <p className='text-indigo-200 text-sm mt-1 ml-9'>Please fill all the required information below</p>
                    </div>

                    {/* Form Body */}
                    <form onSubmit={handleSubmit} className='p-8'>
                        <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                            {inputFields.map((field) => {
                                const Icon = field.icon;
                                return (
                                    <div key={field.id} className='space-y-2'>
                                        <label htmlFor={field.id} className='flex items-center text-sm font-semibold text-white'>
                                            <Icon className='w-4 h-4 mr-2' />
                                            {field.label}
                                            {field.required && <span className='text-red-300 ml-1'>*</span>}
                                        </label>
                                        <div className='relative group'>
                                            <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                                                <Icon className='h-5 w-5 text-indigo-400 group-focus-within:text-indigo-600 transition-colors' />
                                            </div>
                                            <input
                                                type={field.type}
                                                name={field.id}
                                                id={field.id}
                                                value={formData[field.id]}
                                                onChange={handleInputChange}
                                                className='w-full bg-white border-2 border-indigo-200 rounded-xl py-3 pl-10 pr-4 
                                                         text-gray-800 placeholder-gray-400 font-medium
                                                         focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-300 focus:ring-opacity-50
                                                         hover:border-indigo-300 transition-all duration-200'
                                                placeholder={field.placeholder}
                                                required={field.required}
                                            />
                                        </div>
                                        <p className='text-indigo-200 text-xs ml-1'>Enter {field.label.toLowerCase()}</p>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Badge Section */}
                        <div className='mt-6 p-4 bg-indigo-800/30 rounded-xl border border-indigo-400/30'>
                            <div className='flex items-center space-x-2'>
                                <Award className='w-5 h-5 text-yellow-400' />
                                <span className='text-indigo-100 text-sm'>All fields marked with <span className='text-red-300'>*</span> are required</span>
                            </div>
                        </div>

                        {/* Form Actions */}
                        <div className='mt-8 flex flex-col sm:flex-row items-center justify-end space-y-3 sm:space-y-0 sm:space-x-4'>
                            <button
                                type="button"
                                onClick={() => setFormData({
                                    name: '',
                                    phone: '',
                                    fieldofstudy: '',
                                    position: '',
                                    office: '',
                                    currenaddress: ''
                                })}
                                className='w-full sm:w-auto px-6 py-3 bg-white/10 text-white font-medium rounded-xl
                                         hover:bg-white/20 transition-all duration-200
                                         border-2 border-white/30 backdrop-blur-sm
                                         focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-indigo-700'
                            >
                                Clear All
                            </button>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className={`w-full sm:w-auto px-8 py-3 bg-gradient-to-r from-indigo-600 to-indigo-500 
                                         text-white font-bold rounded-xl
                                         hover:from-indigo-700 hover:to-indigo-600 
                                         transition-all duration-200 transform hover:scale-105
                                         focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-indigo-700
                                         disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
                                         flex items-center justify-center space-x-2 shadow-lg border border-white/20`}
                            >
                                {isSubmitting ? (
                                    <>
                                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        <span>Adding Inspector...</span>
                                    </>
                                ) : (
                                    <>
                                        <UserPlus className='w-5 h-5' />
                                        <span>Add Inspector</span>
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>

                {/* Information Card */}
                <div className='mt-6 bg-white/5 backdrop-blur-sm rounded-xl p-5 border border-white/10'>
                    <div className='flex items-start space-x-3'>
                        <div className='flex-shrink-0'>
                            <div className='w-8 h-8 rounded-full bg-indigo-500/30 flex items-center justify-center'>
                                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                </svg>
                            </div>
                        </div>
                        <div>
                            <p className='text-indigo-100 text-sm'>
                                <span className='font-bold text-white'>Important Note:</span> Please ensure all information is accurate before submitting. 
                                The inspector will receive login credentials via email after approval.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Debug Info - Remove in production */}
                {process.env.NODE_ENV === 'development' && (
                    <div className='mt-4 p-4 bg-black/20 backdrop-blur-sm rounded-lg border border-white/10'>
                        <p className='text-xs text-indigo-200'>
                            <strong>Debug:</strong> API URL: {process.env.REACT_APP_API_URL || 'http://localhost:8000/api'} |
                            Token: {localStorage.getItem('token') ? 'Present' : 'Not present'}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}

export default AddInspectors;